import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { href, Link, useFetcher, useOutletContext } from 'react-router'
import { ProductThumbnail } from '~/components/ui/optimized-image'
import Button from '~/components/webshop-button'
import type { LayoutContext } from '~/routes/_layout'
import type { Product } from '~/schemas/products'
import { getPriceColorClass, formatPriceDifference } from '~/utils/pricing'
import { useShopContext } from '~/utils/use-shop-context'

export default function ProductCard({ product }: { product: Product }) {
  const fetcher = useFetcher<{ ok: boolean; error?: string }>()
  const { snapshot, productPricing, selectedB2BCustomer, isShopOpen, canBypassClosedShop } = useOutletContext<LayoutContext>()
  const { isB2B } = useShopContext()

  const entry = snapshot.items.find(i => i.product_id === String(product.id))
  const inCart = !!entry

  const isSubmittingAdd =
    fetcher.state !== 'idle' && fetcher.formData?.get('_intent') === 'add' && fetcher.formData?.get('product_id') === String(product.id)

  // Check if last submission failed (out of stock error)
  const addFailed = fetcher.data && !fetcher.data.ok && fetcher.state === 'idle'

  // Get pricing data for this product
  const pricing = productPricing.get(product.id)
  const displayPrice = pricing?.sellingPrice ?? product.price.amount
  const priceColorClass = pricing ? getPriceColorClass(pricing.priceType) : ''
  const showPriceDiff = pricing && pricing.priceType !== 'default' && pricing.percentageDiff !== null

  const priceCents = Math.round(displayPrice * 100)

  // Admins/webshop owners must select a B2B customer before adding products
  const needsB2BCustomer = isB2B && !selectedB2BCustomer
  // Products awaiting first stock cannot be added to cart (admin only sees these)
  const awaitingStock = (product as Product & { awaitingStock?: boolean }).awaitingStock ?? false
  // Shop closed for regular customers (admins/owners can bypass)
  const shopClosedForUser = !isShopOpen && !canBypassClosedShop
  const canAddToCart = !product.soldOut && !needsB2BCustomer && !awaitingStock && !shopClosedForUser

  // Determine if this product has a special price (discount or custom)
  const hasSpecialPrice = pricing && pricing.priceType !== 'default'

  return (
    <div className="group ws-card-interactive relative flex h-full flex-col overflow-hidden">
      {/* Image area */}
      <Link to={href('/product/:slug', { slug: product.slug })} className="relative flex flex-1 flex-col">
        <div className="relative flex aspect-square items-center justify-center overflow-hidden bg-white p-7">
          {/* Badges container - top right, over the image */}
          <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1.5">
            {product.b2b && (
              <span className="rounded-full bg-(--ws-link) px-2.5 py-1 text-[10px] font-semibold tracking-wider text-white uppercase shadow-xs">
                B2B
              </span>
            )}
            {hasSpecialPrice && (
              <span className="flex items-center gap-1 rounded-full bg-(--ws-accent) px-2.5 py-1 text-[10px] font-semibold tracking-wider text-white uppercase shadow-xs">
                <SparklesIcon className="h-3 w-3" />
                Actie
              </span>
            )}
            {awaitingStock ? (
              <span className="rounded-full bg-blue-500 px-2.5 py-1 text-[10px] font-semibold tracking-wider text-white uppercase shadow-xs">
                Nieuw
              </span>
            ) : product.soldOut ? (
              <span className="rounded-full bg-gray-500 px-2.5 py-1 text-[10px] font-semibold tracking-wider text-white uppercase shadow-xs">
                Uitverkocht
              </span>
            ) : null}
          </div>
          {product.images[0] ? (
            <ProductThumbnail
              src={product.images[0].url}
              alt={product.title}
              className="ease-soft max-h-full max-w-full rounded-(--ws-radius-inner) object-contain transition-transform duration-500 group-hover:scale-[1.04]"
            />
          ) : (
            <div className="flex h-48 w-full items-center justify-center rounded-(--ws-radius-inner) bg-(--ws-surface-2)">
              <svg className="h-16 w-16 opacity-30" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

        {/* Product info section */}
        <div className="border-t border-(--ws-border) p-4">
          <h3 className="line-clamp-2 min-h-[3.5rem] text-[15px] leading-snug font-semibold text-(--ws-text)">{product.title}</h3>

          {/* Price section */}
          <div className="mt-2 flex items-baseline gap-2">
            <span className={`text-xl font-bold tracking-tight tabular-nums ${priceColorClass || 'text-(--ws-heading)'}`}>
              € {displayPrice.toFixed(2)}
            </span>
            {showPriceDiff && <span className="text-xs font-medium text-(--ws-text-muted)">({formatPriceDifference(pricing.percentageDiff)})</span>}
          </div>

          {/* Stock indicator */}
          {!product.soldOut && !awaitingStock && (
            <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-(--ws-success)">
              <span className="h-2 w-2 rounded-full bg-(--ws-success)" />
              Op voorraad
            </div>
          )}
        </div>
      </Link>

      {/* Add to cart button */}
      <div className="p-4 pt-0">
        <fetcher.Form method="post" action={href('/cart')}>
          <input type="hidden" name="_intent" value="add" />
          <input type="hidden" name="product_id" value={product.id} />
          <input type="hidden" name="qty" value="1" />
          <input type="hidden" name="title" value={product.title} />
          <input type="hidden" name="price_cents" value={priceCents} />
          <div title={shopClosedForUser ? 'Deze webshop is momenteel gesloten' : needsB2BCustomer ? 'Selecteer eerst een B2B klant' : ''}>
            <Button
              bgColor={addFailed ? '#dc2626' : undefined}
              bgColorHover={addFailed ? '#b91c1c' : undefined}
              isDone={inCart}
              loading={isSubmittingAdd}
              type="submit"
              disabled={isSubmittingAdd || !canAddToCart || addFailed}
              aria-label={`Toevoegen aan winkelwagen: ${product.title}`}>
              {addFailed ? (
                <span>Uitverkocht</span>
              ) : shopClosedForUser ? (
                <span>Webshop gesloten</span>
              ) : awaitingStock ? (
                <span>Binnenkort leverbaar</span>
              ) : product.soldOut ? (
                <span>Uitverkocht</span>
              ) : isSubmittingAdd ? (
                <span>Toevoegen</span>
              ) : inCart ? (
                <span className="flex items-center justify-center gap-x-2">
                  <CheckIcon className="w-5" /> In winkelwagen
                </span>
              ) : needsB2BCustomer ? (
                <span>Selecteer B2B klant</span>
              ) : (
                <span>In winkelwagen</span>
              )}
            </Button>
          </div>
        </fetcher.Form>
        {addFailed && fetcher.data?.error && <p className="mt-2 text-center text-xs text-red-600">{fetcher.data.error}</p>}
      </div>
    </div>
  )
}
